import React, { useCallback, useMemo } from "react";
import { connect } from "react-redux";

import MarkerSize from "@mapmycustomers/shared/enum/MarkerSize";
import { ActivityPin } from "@mapmycustomers/shared/types/map";

import { Feature } from "@app/component/map/FeaturedMap";
import { getMapSettings } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import { PIN_ZINDEX } from "@app/util/map/consts";
import defaultMapEntryGeometryGetter from "@app/util/map/defaultMapEntryGeometryGetter";
import isValidMapEntry from "@app/util/map/isValidMapEntry";
import defaultActivityStyleGetter from "@app/util/map/markerStyles/defaultActivityStyleGetter";
import typedIdGetter from "@app/util/typedIdGetter";

const isDifferent = (a: ActivityPin, b: ActivityPin) =>
  a.id !== b.id ||
  Math.abs(a.region!.latitude - b.region!.latitude) > Number.EPSILON ||
  Math.abs(a.region!.longitude - b.region!.longitude) > Number.EPSILON;

interface Props {
  entityPins: ActivityPin[];
  mapSettings: {
    markerSize: MarkerSize;
    showEntityIcons: boolean;
  };
  onClick?: (entity: ActivityPin, element?: HTMLElement) => void;
  type?: string;
  zIndex?: number;
}

const ActivityFeature: React.FC<Props> = ({
  entityPins,
  mapSettings,
  onClick,
  type = "entities",
  zIndex = PIN_ZINDEX,
}) => {
  const handleClick = useCallback(
    (entity: ActivityPin, event: google.maps.MapMouseEvent) => {
      event.stop();
      onClick?.(entity, (event.domEvent.target ?? undefined) as HTMLElement | undefined);
    },
    [onClick]
  );

  const entityPinStyleGetter = useMemo(() => {
    return defaultActivityStyleGetter({
      markerSize: mapSettings.markerSize,
      zIndex,
    });
  }, [mapSettings, zIndex]);

  return (
    <>
      <Feature<ActivityPin>
        geometryGetter={defaultMapEntryGeometryGetter}
        idGetter={typedIdGetter}
        isDifferent={isDifferent}
        items={entityPins.filter(isValidMapEntry)}
        onClick={handleClick}
        styleGetter={entityPinStyleGetter}
        type={type}
      />
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  mapSettings: getMapSettings(state),
});

export default connect(mapStateToProps)(ActivityFeature);
